function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import Chart from "../../components/chart";
import { Link } from "react-router-dom";
import { withRouter } from "react-router";
import { Ribbon } from "../../components/analytics-kit";
import { BaseDataViewComponentAsync } from "../../components/base";
import publishSearch from '../../functions/publish-search';
import publishEvent from "../../functions/publish-event";
import ServiceFactory from '../../service';
import { Loader } from '../../components/loaders';
import Error from '../../components/error';
import markupParser from "../../functions/markup-parser";
import { Button } from "@progress/kendo-react-buttons";
import { kwService } from "../keywords/common";
import { Md, Inline, splitSections, saysNothing, cleanText } from "./company-detail";

/*
 * Market Trends report (apps redesign 2026-10-09, board 4).
 * Header (term, "Search interest in <where>, <from> to <to>", Export and
 * Track in Keywords), a ribbon computed from the interest series itself
 * (Interest now, Direction, Year over year, Busiest month), then the
 * interest chart on the left and "What this means", "What to do" and
 * "Related searches" on the right. Related searches are chips; clicking
 * one adds it to Keywords. The remaining report sections sit under the
 * chart.
 *
 * Google Trends reports 0 when a week has too little data, so zeros that
 * sit between non-zero values are treated as missing (a gap in the
 * line), never as a drop.
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
function monthYear(d) {
  const x = new Date(d);
  return isNaN(x.getTime()) ? "" : `${MONTHS[x.getMonth()]} ${x.getFullYear()}`;
}

// The run's `data` field is JSON, with googleTrends sometimes double-encoded.
export function parseTrendData(data) {
  try {
    const inner = typeof data === "string" ? JSON.parse(data) : data || {};
    const gt = typeof inner.googleTrends === "string" ? JSON.parse(inner.googleTrends) : inner.googleTrends;
    return {
      gt: gt || null
    };
  } catch (e) {
    return {
      gt: null
    };
  }
}

/**
 * Weekly interest series [{ d: Date, v: number|null }] from the raw points.
 * Zeros between non-zero values become null (missing), then points are
 * averaged per week (Monday start); a week with no usable points is null.
 */
export function weeklySeries(gt) {
  const raw = (gt && gt.Trends || []).map(r => ({
    d: new Date(r.DateFrom),
    v: r.Value == null ? null : Number(r.Value)
  })).filter(p => !isNaN(p.d.getTime())).sort((a, b) => a.d - b.d);
  const firstNz = raw.findIndex(p => p.v > 0);
  let lastNz = -1;
  raw.forEach((p, i) => {
    if (p.v > 0) lastNz = i;
  });
  raw.forEach((p, i) => {
    if ((p.v === 0 || isNaN(p.v)) && i > firstNz && i < lastNz) p.v = null;
  });
  const buckets = new Map();
  raw.forEach(p => {
    const day = new Date(p.d.getFullYear(), p.d.getMonth(), p.d.getDate());
    day.setDate(day.getDate() - (day.getDay() + 6) % 7);
    const key = day.getTime();
    const b = buckets.get(key) || {
      d: day,
      sum: 0,
      n: 0
    };
    if (p.v != null && !isNaN(p.v)) {
      b.sum += p.v;
      b.n += 1;
    }
    buckets.set(key, b);
  });
  return [...buckets.values()].sort((a, b) => a.d - b.d).map(b => ({
    d: b.d,
    v: b.n ? b.sum / b.n : null
  }));
}
const avg = a => a.length ? a.reduce((s, v) => s + v, 0) / a.length : 0;

/**
 * Direction from the series: last 4 points vs the peak and vs the first 4.
 * Returns { word, pct, line } or null.
 */
export function trendDirection(series) {
  const pts = series.filter(p => p.v != null);
  if (pts.length < 8) return null;
  const vals = pts.map(p => p.v);
  const first = avg(vals.slice(0, 4));
  const last = avg(vals.slice(-4));
  const peak = Math.max(...vals);
  const pct = r => Math.round((r - 1) * 100);
  const vsStart = first > 0 ? last / first : null;
  const vsPeak = peak > 0 ? last / peak : 1;
  if (vsPeak < 0.8) {
    if (vsStart === null || vsStart >= 0.9) return {
      word: "Cooling",
      pct: pct(vsPeak),
      line: `down ${Math.abs(pct(vsPeak))}% from the peak`
    };
    return {
      word: "Falling",
      pct: pct(vsStart),
      line: `down ${Math.abs(pct(vsStart))}% from the start`
    };
  }
  if (vsStart === null) return {
    word: "Rising",
    pct: null,
    line: "up from almost no searches"
  };
  if (vsStart > 1.1) return {
    word: "Rising",
    pct: pct(vsStart),
    line: `up ${pct(vsStart)}% from the start`
  };
  if (vsStart < 0.9) return {
    word: "Falling",
    pct: pct(vsStart),
    line: `down ${Math.abs(pct(vsStart))}% from the start`
  };
  const p = pct(vsStart);
  return {
    word: "Steady",
    pct: p,
    line: p === 0 ? "about the same as the start" : `${p > 0 ? "up" : "down"} ${Math.abs(p)}% from the start`
  };
}

/** Last 4 weeks vs the same 4 weeks a year earlier, when the series reaches back that far. */
function yearOverYear(series) {
  const pts = series.filter(p => p.v != null);
  if (pts.length < 8) return null;
  const lastPts = pts.slice(-4);
  const end = lastPts[lastPts.length - 1].d.getTime();
  const start = lastPts[0].d.getTime();
  const yr = 364 * 86400000;
  const prior = pts.filter(p => p.d.getTime() >= start - yr - 3 * 86400000 && p.d.getTime() <= end - yr + 3 * 86400000);
  if (prior.length < 2 || pts[0].d.getTime() > start - yr + 3 * 86400000) return null;
  const a = avg(prior.map(p => p.v));
  if (!a) return null;
  const pct = Math.round((avg(lastPts.map(p => p.v)) / a - 1) * 100);
  return {
    word: pct > 5 ? "Up" : pct < -5 ? "Down" : "Flat",
    pct
  };
}

/** Calendar month with the highest average interest (across years). */
function busiestMonth(series) {
  const pts = series.filter(p => p.v != null);
  const months = new Set(pts.map(p => `${p.d.getFullYear()}-${p.d.getMonth()}`));
  if (months.size < 3) return null;
  const by = new Map();
  pts.forEach(p => {
    const m = p.d.getMonth();
    const b = by.get(m) || [];
    b.push(p.v);
    by.set(m, b);
  });
  let best = null;
  by.forEach((vals, m) => {
    const a = avg(vals);
    if (!best || a > best.a) best = {
      m,
      a
    };
  });
  return best ? MONTHS_LONG[best.m] : null;
}

// Words in the stored summary that point one way or the other.
const UP_WORDS = /\b(ris(e|es|ing)|rose|grow(s|ing|th)?|grew|increas\w*|climb\w*|surg\w*|upward)\b/i;
const DOWN_WORDS = /\b(fall(s|ing)?|fell|declin\w*|decreas\w*|dropp?\w*|cool(s|ed|ing)?|wan(e|ing)|downward)\b/i;
const FLAT_WORDS = /\b(steady|stable|flat|consistent)\b/i;
function contradicts(word, text) {
  const t = String(text || "");
  const up = UP_WORDS.test(t),
    down = DOWN_WORDS.test(t),
    flat = FLAT_WORDS.test(t);
  if (word === "Rising") return down && !up;
  if (word === "Falling" || word === "Cooling") return up && !down;
  if (word === "Steady") return (up || down) && !flat;
  return false;
}

// Related-search chip candidates: list items and bold phrases.
function chipCandidates(md) {
  const out = [];
  const push = t => {
    const term = cleanText(t).replace(/\*\*/g, "").replace(/\[([^\]]*)\]\([^)]*\)/g, "$1").replace(/["“”]/g, "").split(/[:—–(]|\s-\s/)[0].trim().toLowerCase();
    if (term && term.length >= 3 && term.length <= 40 && !out.includes(term)) out.push(term);
  };
  (md.match(/^\s*([-*•]|\d+[.)])\s+(.+)$/gm) || []).forEach(l => push(l.replace(/^\s*([-*•]|\d+[.)])\s+/, "")));
  (md.match(/\*\*([^*]{3,40})\*\*/g) || []).forEach(b => push(b));
  return out.slice(0, 10);
}

// Short action list from the Recommendations section.
function actionItems(md) {
  const items = (md.match(/^\s*([-*•]|\d+[.)])\s+(.+)$/gm) || []).map(l => l.replace(/^\s*([-*•]|\d+[.)])\s+/, "").trim());
  if (items.length) return items.slice(0, 5);
  return (cleanText(md).replace(/\n+/g, " ").match(/[^.!?]+[.!?]+/g) || []).map(s => s.trim()).filter(s => s.length > 8).slice(0, 4);
}
const SECTION_KEYS = /demand|summary|timeline|volume|competition|related|developments|recommend/i;
const plainTrendTitle = t => /demand|summary/i.test(t) ? "What this means" : /recommend/i.test(t) ? "What to do" : /related/i.test(t) ? "Related searches" : /timeline/i.test(t) ? "Peaks and dips" : /volume|competition/i.test(t) ? "Search volume and cost" : /development/i.test(t) ? "What is changing in the market" : t;

/** Single-line interest chart with gaps for missing weeks and one label per month. */
// One point per month: the average of the weeks that have data. Used when a term's weekly series is mostly empty
// (Google Trends reports 0 for weeks with too little data), so the chart stays a readable line instead of dots.
export function monthlySeries(series) {
  const by = new Map();
  series.forEach(p => {
    const d = new Date(p.d.getFullYear(), p.d.getMonth(), 15);
    const k = d.getTime();
    const b = by.get(k) || {
      d,
      sum: 0,
      n: 0
    };
    if (p.v != null && !isNaN(p.v)) {
      b.sum += p.v;
      b.n += 1;
    }
    by.set(k, b);
  });
  return [...by.values()].sort((a, b) => a.d - b.d).map(b => ({
    d: b.d,
    v: b.n ? b.sum / b.n : null
  }));
}
class MainComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "track", (term, topicGroup) => async () => {
      if (this.state.busyTerm || this.isTracked(term)) return;
      this.setState({
        busyTerm: term
      });
      try {
        await kwService("").postAsync([{
          term,
          engines: "google",
          topicGroup: topicGroup || null
        }]);
        this.setState({
          busyTerm: null,
          tracked: [...(this.state.tracked || []), term.toLowerCase()],
          notice: `"${term}" is now tracked in Keywords. Weekly ranking snapshots start on the next cycle.`
        });
      } catch (e) {
        this.setState({
          busyTerm: null,
          notice: `Could not track "${term}". Try again in a moment.`
        });
      }
    });
    _defineProperty(this, "openKeywords", () => {
      this.props.history.push("/Keywords");
    });
    _defineProperty(this, "onExportClick", () => {
      let data = this.props.data;
      let content = markupParser(cleanText(data.text));
      let fileName = String(data.term || 'trend').replace(/ /g, '_') + '_trends.doc';
      const header = `
    <html xmlns:o='urn:schemas-microsoft-com:office:office'
          xmlns:w='urn:schemas-microsoft-com:office:word'
          xmlns='http://www.w3.org/TR/REC-html40'>
    <head><meta charset='utf-8'><title>${data.term} Trends</title></head><body>`;
      const fullHTML = header + content + '</body></html>';
      const blob = new Blob(['﻿', fullHTML], {
        type: 'application/msword'
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
    this.state = {
      tracked: null,
      busyTerm: null,
      notice: null
    };
    this._alive = true;
  }
  componentDidMount() {
    publishSearch('Research');
    publishEvent('reset-search', {});
    // Which terms are already tracked in Keywords? Fail-soft.
    kwService("").getAsync().then(rows => {
      if (this._alive) this.setState({
        tracked: (rows || []).map(r => String(r.term).toLowerCase())
      });
    }).catch(() => {
      if (this._alive) this.setState({
        tracked: []
      });
    });
  }
  componentWillUnmount() {
    this._alive = false;
  }
  isTracked(term) {
    return (this.state.tracked || []).includes(String(term || "").toLowerCase());
  }
  render() {
    const data = this.props.data;
    const {
      notice,
      busyTerm,
      tracked
    } = this.state;
    const {
      gt
    } = parseTrendData(data.data);
    const series = weeklySeries(gt);
    const known = series.filter(p => p.v != null);
    const dir = trendDirection(series);
    const yoy = yearOverYear(series);
    const busiest = busiestMonth(series);
    const nowPt = known.length ? known[known.length - 1] : null;
    const peakPt = known.length ? known.reduce((b, p) => p.v > b.v ? p : b, known[0]) : null;
    const termTracked = this.isTracked(data.term);
    const sections = splitSections(data.text || "", SECTION_KEYS).filter(s => s.title).map(s => ({
      ...s,
      label: plainTrendTitle(s.title)
    }));
    const pick = label => sections.find(s => s.label === label);
    const summary = pick("What this means");
    const recs = pick("What to do");
    const related = pick("Related searches");
    const rest = sections.filter(s => ![summary, recs, related].includes(s) && !saysNothing(s.body));
    const chips = related ? chipCandidates(related.body) : [];
    const actions = recs ? actionItems(recs.body) : [];
    const fallback = !sections.length && data.text ? data.text : null;
    const stale = dir && summary && contradicts(dir.word, summary.body);
    const loc = data.location || "the United States";
    const where = /^united states$/i.test(loc) ? "the United States" : loc;
    const period = data.fromDate && data.toDate ? `, ${monthYear(data.fromDate)} to ${monthYear(data.toDate)}` : "";
    const pctText = p => p == null ? null : `${p > 0 ? "+" : ""}${p}%`;
    const tiles = known.length ? [{
      l: "Interest now",
      v: String(Math.round(nowPt.v)),
      s: `of 100${peakPt ? `, peak in ${MONTHS[peakPt.d.getMonth()]}` : ""}`
    }, dir ? {
      l: "Direction",
      v: dir.word,
      s: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
        className: dir.pct > 0 ? "apr-up" : dir.pct < 0 ? "apr-down" : ""
      }, pctText(dir.pct)), " ", dir.line)
    } : null, yoy ? {
      l: "Year over year",
      v: yoy.word,
      s: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
        className: yoy.pct > 0 ? "apr-up" : yoy.pct < 0 ? "apr-down" : ""
      }, pctText(yoy.pct)), " vs last year")
    } : null, busiest ? {
      l: "Busiest month",
      v: busiest
    } : null].filter(Boolean) : [];
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view apr-root apr-trend-report"
    }, /*#__PURE__*/React.createElement("header", {
      className: "apr-hdr"
    }, /*#__PURE__*/React.createElement("div", {
      className: "apr-hdr-text"
    }, /*#__PURE__*/React.createElement("span", {
      className: "apr-crumb"
    }, /*#__PURE__*/React.createElement(Link, {
      to: "/Apps"
    }, "Apps"), " \xB7 ", /*#__PURE__*/React.createElement(Link, {
      to: "/Research/Trends"
    }, "Market Trends")), /*#__PURE__*/React.createElement("h1", {
      className: "apr-cap"
    }, data.term), /*#__PURE__*/React.createElement("span", {
      className: "apr-sub"
    }, "Search interest in ", where, period)), /*#__PURE__*/React.createElement("div", {
      className: "apr-actions"
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: this.onExportClick
    }, "Export"), termTracked ? /*#__PURE__*/React.createElement(Button, {
      onClick: this.openKeywords
    }, "Open in Keywords") : /*#__PURE__*/React.createElement(Button, {
      disabled: busyTerm === data.term || tracked === null,
      onClick: this.track(data.term, null),
      title: "Adds this term to Keywords for weekly ranking snapshots"
    }, busyTerm === data.term ? "Adding…" : "Track in Keywords"))), notice && /*#__PURE__*/React.createElement("div", {
      className: "apr-notice"
    }, notice, " ", /*#__PURE__*/React.createElement("a", {
      onClick: () => this.setState({
        notice: null
      })
    }, "Dismiss")), tiles.length ? /*#__PURE__*/React.createElement(Ribbon, {
      comparing: null,
      tiles: tiles,
      cols: tiles.length === 4 ? 4 : tiles.length === 3 ? 3 : undefined
    }) : null, /*#__PURE__*/React.createElement("div", {
      className: "apr-trend-grid"
    }, /*#__PURE__*/React.createElement("div", {
      className: "apr-sections"
    }, known.length >= 2 ? /*#__PURE__*/React.createElement("section", {
      className: "apr-card"
    }, /*#__PURE__*/React.createElement("b", {
      className: "apr-card-title"
    }, "Interest over time"), (() => {
      // Same chart as Website Analytics (hover shows the value). Only weeks with data are plotted;
      // a term with mostly empty weeks is shown by month.
      const gaps = series.filter(p => p.v == null).length;
      const monthly = series.length > 8 && gaps / series.length > 0.25;
      const src = monthly ? monthlySeries(series) : series;
      const pts = src.filter(p => p.v != null);
      const fmt = d => d.toLocaleDateString("en-US", monthly ? {
        month: "short",
        year: "numeric"
      } : {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
      return /*#__PURE__*/React.createElement(Chart, {
        width: "100%",
        height: "260px",
        chartType: "LineChart",
        data: [["When", "Interest (of 100)"], ...pts.map(p => [fmt(p.d), Math.round(p.v)])],
        options: {
          colors: ["#ff6358"],
          legend: {
            position: "none"
          },
          vAxis: {
            viewWindow: {
              min: 0
            }
          },
          pointSize: pts.length > 40 ? 0 : 5,
          hAxis: {
            showTextEvery: Math.max(1, Math.ceil(pts.length / 12))
          },
          tooltip: {
            format: "{0:n0}"
          }
        }
      });
    })(), /*#__PURE__*/React.createElement("span", {
      className: "apr-foot"
    }, "Weeks with too little search data are skipped rather than drawn as zero.")) : null, rest.map((s, i) => /*#__PURE__*/React.createElement("section", {
      className: "apr-card apr-text",
      key: i
    }, /*#__PURE__*/React.createElement("b", null, s.label), /*#__PURE__*/React.createElement(Md, {
      source: s.body
    }))), fallback ? /*#__PURE__*/React.createElement("section", {
      className: "apr-card apr-text"
    }, /*#__PURE__*/React.createElement(Md, {
      source: fallback
    })) : null), /*#__PURE__*/React.createElement("div", {
      className: "apr-sections"
    }, summary ? /*#__PURE__*/React.createElement("section", {
      className: "apr-card apr-text"
    }, /*#__PURE__*/React.createElement("b", null, "What this means"), /*#__PURE__*/React.createElement(Md, {
      source: summary.body
    }), stale ? /*#__PURE__*/React.createElement("span", {
      className: "apr-stale"
    }, "The summary was written when the report ran; the numbers above are current.") : null) : null, actions.length ? /*#__PURE__*/React.createElement("section", {
      className: "apr-card apr-text"
    }, /*#__PURE__*/React.createElement("b", null, "What to do"), /*#__PURE__*/React.createElement("ul", {
      className: "apr-todo"
    }, actions.map((a, i) => /*#__PURE__*/React.createElement("li", {
      key: i
    }, /*#__PURE__*/React.createElement(Inline, {
      text: a
    }))))) : null, related ? /*#__PURE__*/React.createElement("section", {
      className: "apr-card apr-text"
    }, /*#__PURE__*/React.createElement("b", null, "Related searches"), chips.length ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "apr-chips"
    }, chips.map(c => {
      const on = this.isTracked(c);
      return /*#__PURE__*/React.createElement("button", {
        key: c,
        type: "button",
        className: "apr-tchip" + (on ? " on" : ""),
        disabled: on || !!busyTerm,
        onClick: this.track(c, data.term),
        title: on ? "Already tracked in Keywords" : "Track in Keywords"
      }, busyTerm === c ? "Adding…" : on ? `${c} ✓` : `${c} +`);
    })), /*#__PURE__*/React.createElement("span", {
      className: "apr-foot"
    }, "Click a search to track it in Keywords.")) : saysNothing(related.body) ? /*#__PURE__*/React.createElement("span", {
      className: "apr-muted"
    }, "No related searches were found for this term.") : /*#__PURE__*/React.createElement(Md, {
      source: related.body
    })) : null)));
  }
}
export class MarketTrendsDetailViewComponent extends BaseDataViewComponentAsync {
  constructor(props) {
    super(props);
  }
  async fetchData() {
    var Service = ServiceFactory.getType('/market-trends/' + this.props.params.id, 'research');
    var service = new Service();
    try {
      var data = await service.getAsync();
      this.allowRender = true;
      this.data = data;
      this.allowUpdate = false;
      this.forceUpdate();
    } catch (err) {
      this.error = true;
      this.errorMessage = err.message;
      this.allowRender = true;
      this.allowUpdate = false;
      this.forceUpdate();
    }
  }
  render() {
    if (!this.allowRender) {
      return /*#__PURE__*/React.createElement(Loader, null);
    } else if (this.error) {
      return /*#__PURE__*/React.createElement(Error, {
        message: this.errorMessage
      });
    } else {
      return /*#__PURE__*/React.createElement(RoutableComponent, {
        data: this.data,
        params: this.props.params
      });
    }
  }
}
const RoutableComponent = withRouter(MainComponent);
export default {
  MarketTrendsDetailViewComponent
};